import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  loginWithPasskey,
  isPlatformAuthenticatorAvailable,
  isPasskeySupported,
  createQRSession,
  pollQRSession
} from '../services/passkeyService';
import { QRCodeSVG } from 'qrcode.react';

export const PasskeyLogin = ({ email = null }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    checkSupport();
  }, []);

  useEffect(() => {
    let pollInterval;
    if (polling && qrData) {
      pollInterval = setInterval(async () => {
        try {
          const status = await pollQRSession(qrData.sessionId);
          if (status.status === 'completed') {
            setPolling(false);
            clearInterval(pollInterval);

            // Store token and login
            if (status.token) {
              setUser(status.user, status.token);
              navigate('/');
            }
          } else if (status.status === 'failed') {
            setPolling(false);
            clearInterval(pollInterval);
            setError(status.error || 'QR authentication failed');
          }
        } catch (err) {
          console.error('QR poll error:', err);
        }
      }, 2000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        setPolling(false);
        if (pollInterval) clearInterval(pollInterval);
        setError('QR code expired. Please try again.');
      }, 300000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [polling, qrData, setUser, navigate]);

  const checkSupport = async () => {
    const supported = isPasskeySupported();
    const platformAuth = await isPlatformAuthenticatorAvailable();
    setIsSupported(supported);
    setHasPlatformAuth(platformAuth);

    // If no platform authenticator, offer QR code immediately
    if (supported && !platformAuth) {
      // User can still try direct login or use QR
    }
  };

  const handleDirectLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      setShowQR(false);

      const result = await loginWithPasskey(email);

      if (result.token) {
        setUser(result.user, result.token);
        navigate('/');
      }
    } catch (err) {
      console.error('Passkey login error:', err);

      // If error suggests no authenticator, offer QR code
      if (err.message.includes('authenticator') || err.message.includes('not available')) {
        setError('No biometric authenticator found. Use QR code instead?');
        // Don't auto-show QR, let user choose
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQRLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      setShowQR(true);

      const qrSession = await createQRSession('login', email);
      setQrData(qrSession);
      setPolling(true);
    } catch (err) {
      console.error('QR login error:', err);
      setError(err.message || 'Failed to create QR code. Please try again.');
      setShowQR(false);
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-brown-800 mb-4">Browser Not Supported</h2>
          <p className="text-brown-600 mb-4">
            Your browser doesn't support passkeys. Please use password login or try:
          </p>
          <ul className="text-left text-brown-600 mb-4">
            <li>• Safari on iOS/macOS</li>
            <li>• Chrome on Android/Windows/Mac</li>
            <li>• Edge on Windows</li>
          </ul>
          <button
            onClick={() => navigate('/login')}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full"
          >
            Use Password Login
          </button>
        </div>
      </div>
    );
  }

  if (showQR && qrData) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-brown-800 mb-4">Scan with Your Phone</h2>
          <p className="text-brown-600 mb-6">
            Use your phone's camera to scan this QR code and authenticate with Face ID/Touch ID
          </p>

          <div className="bg-white p-4 rounded-lg inline-block mb-6">
            <QRCodeSVG
              value={qrData.qrData}
              size={256}
              level="M"
              includeMargin={true}
            />
          </div>

          {polling && (
            <div className="mb-4">
              <div className="animate-pulse text-blue-600 font-semibold">
                Waiting for phone authentication...
              </div>
              <div className="text-sm text-brown-500 mt-2">
                This QR code expires in 5 minutes
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <button
            onClick={() => {
              setShowQR(false);
              setPolling(false);
              setQrData(null);
              setError(null);
            }}
            className="bg-gray-300 hover:bg-gray-400 text-brown-800 font-bold py-2 px-4 rounded-full"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <div className="text-center mb-6">
        <div className="text-4xl mb-4">🔐</div>
        <h2 className="text-2xl font-bold text-brown-800 mb-2">
          Sign In with {hasPlatformAuth ? 'Face ID/Touch ID' : 'Passkey'}
        </h2>
        <p className="text-brown-600">
          Quick and secure authentication without passwords
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {hasPlatformAuth && (
          <button
            onClick={handleDirectLogin}
            disabled={loading}
            className="w-full bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              'Authenticating...'
            ) : (
              <>
                <span className="text-xl mr-2">👆</span>
                Sign In with {hasPlatformAuth ? 'Biometrics' : 'Passkey'}
              </>
            )}
          </button>
        )}

        <button
          onClick={handleQRLogin}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <span className="text-xl mr-2">📱</span>
          {hasPlatformAuth ? 'Or Use Phone (QR Code)' : 'Sign In with Phone'}
        </button>

        <button
          onClick={() => navigate('/login')}
          className="w-full bg-gray-300 hover:bg-gray-400 text-brown-800 font-bold py-3 px-4 rounded-full"
        >
          Use Password Instead
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-brown-500">
          Passkeys are more secure than passwords and sync across your devices
        </p>
      </div>
    </div>
  );
};

export default PasskeyLogin;
